import React, { useState, useEffect } from "react";
import { 
  Table, Tag, Typography, Input, Select, Button, Row, Col, Card, 
  Spin, Empty, Space, message, DatePicker 
} from "antd";
import { 
  AppleOutlined, 
  AndroidOutlined, 
  WindowsOutlined, 
  ChromeOutlined, 
  MobileOutlined,
  DesktopOutlined,
  QuestionOutlined,
  FilterOutlined,
  ReloadOutlined,
  CalendarOutlined
} from "@ant-design/icons";
import axiosInstance from "../../Config/axiosConfig";
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

const EventsLogs = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  // Initialize with today's date range by default
  const [filters, setFilters] = useState({
    eventLogId: null,
    username: null,
    dateRange: {
      startDate: dayjs().startOf('day').toISOString(),
      endDate: dayjs().endOf('day').toISOString()
    },
    userRoleId: null,
  });

// Load events on mount (after lookups are loaded)
useEffect(() => {
  if (!lookupsLoading) {
    // Load with today's date as default
    const todayRange = {
      startDate: dayjs().startOf('day').toISOString(),
      endDate: dayjs().endOf('day').toISOString()
    };
    loadEvents({ dateRange: todayRange });
  }
}, [lookupsLoading]);

  // State for lookups
  const [eventTypes, setEventTypes] = useState([]);
  const [userRoles, setUserRoles] = useState([]);

  const storedDeviceId = localStorage.getItem("deviceId");
  const storedUserIp = localStorage.getItem("userIp");

  // Fetch lookups data (event types and user roles)
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        setLookupsLoading(true);
        const response = await axiosInstance.post('Public/get-lookups');
        console.log("lookups response:", response.data);
        const data = response.data;
        
        if (data.code === 200 && data.data) {
          // Set event types from API
          if (data.data.userEventTypes && Array.isArray(data.data.userEventTypes)) {
            const formattedEventTypes = data.data.userEventTypes.map(event => ({
              id: event.id,
              name: event.name,
              // Improved logic for determining event type
              type: determineEventType(event.name)
            }));
            setEventTypes(formattedEventTypes);
            console.log("Event types loaded:", formattedEventTypes);
          }

          // Set user roles from API (if available)
          if (data.data.userRoles && Array.isArray(data.data.userRoles)) {
            const formattedRoles = data.data.userRoles.map(role => ({
              id: role.id,
              name: role.name,
              color: getRoleColorByName(role.name)
            }));
            setUserRoles(formattedRoles);
          } else {
            // Use default roles if not in API
            setUserRoles(getDefaultUserRoles());
          }
        }
      } catch (error) {
        console.error('Error fetching lookups:', error);
        message.error("Failed to load event types and roles");
        
        // Set defaults on error
        setUserRoles(getDefaultUserRoles());
      } finally {
        setLookupsLoading(false);
      }   
    };

    fetchLookups();
  }, []);

  // Improved event type determination
  const determineEventType = (eventName) => {
    if (!eventName) return 'default';
    
    const lowerName = eventName.toLowerCase();
    
    // Success patterns
    if (lowerName.includes('success') || 
        lowerName.includes('completed') || 
        lowerName.includes('created') ||
        lowerName.includes('updated') ||
        lowerName.includes('verified') ||
        lowerName.includes('approved') ||
        lowerName.includes('login') ||
        lowerName.includes('registered')) {
      return 'success';
    }
    
    // Fail/Error patterns
    if (lowerName.includes('fail') || 
        lowerName.includes('error') || 
        lowerName.includes('rejected') ||
        lowerName.includes('blocked') ||
        lowerName.includes('invalid') ||
        lowerName.includes('unauthorized') ||
        lowerName.includes('expired')) {
      return 'fail';
    }
    
    // Warning patterns
    if (lowerName.includes('warning') || 
        lowerName.includes('pending') || 
        lowerName.includes('attempt')) {
      return 'warning';
    }
    
    return 'default';
  };

  // Get role color by role name
  const getRoleColorByName = (roleName) => {
    if (!roleName) return 'orange';
    
    const lowerRole = roleName.toLowerCase();
    
    if (lowerRole.includes('admin')) return 'red';
    if (lowerRole.includes('student')) return 'blue';
    if (lowerRole.includes('teacher')) return 'green';
    if (lowerRole.includes('user')) return 'cyan';
    if (lowerRole.includes('moderator')) return 'purple';
    
    return 'orange';
  };

  // Default user roles if API fails
  const getDefaultUserRoles = () => {
    return [
      { id: 1, name: "Admin", color: "red" },
      { id: 2, name: "Students", color: "blue" },
      { id: 3, name: "Teacher", color: "green" },
      { id: 4, name: "User", color: "cyan" },
    ];
  };

  // Get event type by ID from loaded eventTypes
  const getEventTypeById = (id) => {
    if (!id) return "Unknown Event";
    const eventType = eventTypes.find(event => event.id === id);
    return eventType ? eventType.name : `Unknown Event (${id})`;
  };

  // Get event type color by ID
  const getEventColorById = (id) => {
    if (!id) return "default";
    
    const eventType = eventTypes.find(event => event.id === id);
    if (!eventType) return "default";
    
    // Map event types to Ant Design Tag colors
    switch(eventType.type) {
      case "success":
        return "success"; // Green
      case "fail":
        return "error";   // Red
      case "warning":
        return "warning"; // Orange/Gold
      default:
        return "processing"; // Blue (for default/processing)
    }
  };

  // Get role name by ID
  const getRoleNameById = (id) => {
    if (!id) return "Unknown Role";
    const role = userRoles.find(role => role.id === id);
    return role ? role.name : `Unknown Role (${id})`;
  };

  // Get role color by ID
  const getRoleColorById = (id) => {
    if (!id) return "orange";
    const role = userRoles.find(role => role.id === id);
    return role ? role.color : "orange";
  };

// LOAD EVENTS
const loadEvents = async (filtersToApply = {}) => {
  try {
    setLoading(true);

    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      message.error("Authentication required. Please login first.");
      setLoading(false);
      return;
    }

    // Wait for lookups to load if still loading
    if (lookupsLoading) {
      message.info("Loading event types...");
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Use provided filters or default to current state
    const appliedFilters = Object.keys(filtersToApply).length > 0 
      ? filtersToApply 
      : filters;

    // Construct payload
    const payload = {
      eventLogId: appliedFilters.eventLogId || null,
      username: appliedFilters.username || null,
      dateRange: appliedFilters.dateRange || null,
      userRoleId: appliedFilters.userRoleId || null
    };

    console.log("Loading events with filters:", payload);

    // Remove null/undefined values (except dateRange which has default)
    Object.keys(payload).forEach(key => {
      if (payload[key] === null || payload[key] === undefined || payload[key] === "") {
        delete payload[key];
      }
    });

      const response = await axiosInstance.post(
        "Admin/get-event-logs",
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'UserDevice': storedDeviceId,
            'UserIPAddress': storedUserIp,
          },
        }
      );

      const data = response.data;

      if (data.code !== 200 || !Array.isArray(data.data)) {
        message.error(data.message || "Unexpected API response");
        return;
      }

      // Format results
      const formatted = data.data.map(item => {
        const username = item.username;
        const isEmail = username?.includes("@");

        return {
          eventLogId: item.eventCreateAt,
          eventTypeId: item.eventTypeId || item.eventLogId,
          username,
          email: isEmail ? username : null,
          phonenumber: !isEmail ? username : null,
          role: getRoleNameById(item.roleId),
          roleId: item.roleId,
          event_type: getEventTypeById(item.eventTypeId || item.eventLogId),
          description: item.eventDescription,
          ip_address: item.deviceIp,
          device: item.device,
          created_at: item.eventCreatedDateAt,
          name: item.name
        };
      });

      setEvents(formatted);
      setFilteredEvents(formatted);

    } catch (error) {
      console.error("Error loading events:", error);
      if (error.response) {
        if (error.response.status === 401) message.error("Session expired. Please login again.");
        else if (error.response.status === 403) message.error("You don't have permission to view event logs.");
        else message.error(error.response.data?.message || "Failed to load events");
      } else {
        message.error("Network or server error. Try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Load events on mount (after lookups are loaded)
  useEffect(() => {
    if (!lookupsLoading) {
      loadEvents();
    }
  }, [lookupsLoading]);

  // Handle individual filter changes
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    
    // Only send request if we have a value (not null/undefined/empty)
    if (value !== null && value !== undefined && value !== "") {
      loadEvents(newFilters);
    } else {
      // If filter is cleared, reload without that filter
      const filtersToSend = { ...newFilters };
      delete filtersToSend[filterType];
      loadEvents(filtersToSend);
    }
  };

  // Handle event ID filter
  const handleEventIdFilter = (value) => {
    const eventLogId = value ? parseInt(value) : null;
    handleFilterChange("eventLogId", eventLogId);
  };

  // Handle username filter
  const handleUsernameFilter = (value) => {
    handleFilterChange("username", value);
  };

// Handle date range filter
const handleDateRangeFilter = (dates) => {
  if (dates && dates.length === 2) {
    const dateRange = {
      startDate: dates[0].toISOString(),
      endDate: dates[1].toISOString()
    };
    handleFilterChange("dateRange", dateRange);
  } else {
    // If cleared, set back to today
    const todayRange = {
      startDate: dayjs().startOf('day').toISOString(),
      endDate: dayjs().endOf('day').toISOString()
    };
    handleFilterChange("dateRange", todayRange);
  }
};

  // Handle role ID filter
  const handleRoleIdFilter = (value) => {
    const userRoleId = value ? parseInt(value) : null;
    handleFilterChange("userRoleId", userRoleId);
  };

  // RESET ALL FILTERS
  const handleResetFilters = () => {
    const todayRange = {
      startDate: dayjs().startOf('day').toISOString(),
      endDate: dayjs().endOf('day').toISOString()
    };
    
    setFilters({
      eventLogId: null,
      username: null,
      dateRange: todayRange,
      userRoleId: null,
    });
    
    // Load events with today's date
    loadEvents({
      eventLogId: null,
      username: null,
      dateRange: todayRange,
      userRoleId: null,
    });
  };

  // Get event IDs for dropdown (from loaded eventTypes)
  const eventIdOptions = eventTypes.map(event => ({
    id: event.id,
    name: event.name,
    type: event.type
  }));

  // Get role IDs for dropdown (from loaded userRoles)
  const roleIdOptions = userRoles.map(role => ({
    id: role.id,
    name: role.name
  }));

  // DEVICE ICON HANDLER
  const getDeviceIcon = (device) => {
    if (!device) return <QuestionOutlined />;
    
    const os = device?.os?.family?.toLowerCase() || "";
    const ua = device?.ua?.family?.toLowerCase() || "";
    const family = device?.device?.family?.toLowerCase() || "";

    if (os.includes("ios") || os.includes("mac")) return <AppleOutlined />;
    if (os.includes("android")) return <AndroidOutlined />;
    if (os.includes("windows")) return <WindowsOutlined />;
    if (ua.includes("chrome")) return <ChromeOutlined />;
    if (family.includes("mobile")) return <MobileOutlined />;

    return <DesktopOutlined />;
  };

  const formatDeviceInfo = (device) => {
    if (!device) return "Unknown Device";
    const parts = [];
    if (device.os?.family) parts.push(device.os.family);
    if (device.ua?.family) parts.push(device.ua.family);
    return parts.length > 0 ? parts.join(" · ") : "Unknown Device";
  };

  // TABLE COLUMNS
  const columns = [
    {
      title: "#",
      width: 50,
      render: (text, record, index) => index + 1,
    },
    {
      title: "Event Date",
      dataIndex: "eventLogId",
      width: 100,
      render: (id) => <Tag color="blue">#{id}</Tag>,
      sorter: (a, b) => a.eventLogId - b.eventLogId,
    },
    {
      title: "Event Type",
      dataIndex: "event_type",
      render: (text, record) => {
        // Use eventTypeId for color lookup, fallback to eventLogId
        const eventId = record.eventTypeId || record.eventLogId;
        return (
          <Tag color={getEventColorById(eventId)}>
            {text}
          </Tag>
        );
      }
    },
    {
      title: "Description",
      dataIndex: "description",
      width: 120,
      render: (text) => (
        <Typography.Paragraph style={{ margin: 0, fontSize: 12 }}>
          {text}
        </Typography.Paragraph>
      )
    },
    {
      title: "Username",
      dataIndex: "username",
      render: (text) => <Typography.Text strong>{text}</Typography.Text>,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text) => text || "N/A",
    },
    {
      title: "Role",
      dataIndex: "role",
      render: (role, record) => (
        <Tag color={getRoleColorById(record.roleId)}>
          {role}
        </Tag>
      )
    },
    {
      title: "IP",
      dataIndex: "ip_address",
      render: (ip) => <Tag color="purple">{ip}</Tag>,
    },
    {
      title: "Device",
      dataIndex: "device",
      render: (device) => (
        <Space>
          {getDeviceIcon(device)}
          <Typography.Text type="secondary">
            {formatDeviceInfo(device)}
          </Typography.Text>
        </Space>
      )
    },
  ];

  // Show loading while fetching lookups
  if (lookupsLoading) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
            Loading event types and roles...
          </Typography.Text>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Event Logs</span>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {/* Color Legend */}
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Tag color="success">Success</Tag>
              <Tag color="error">Fail</Tag>
              <Tag color="warning">Warning</Tag>
              <Tag color="processing">Other</Tag>
            </div>
            
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {eventTypes.length} event types loaded
            </Typography.Text>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => loadEvents(filters)} 
              loading={loading}
              size="middle"
            >
              Reload
            </Button>
          </div>
        </div>
      }
    >
      {/* FILTERS ROW */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {/* Event Type Filter */}
        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Select Event Type"
            style={{ width: "100%" }}
            value={filters.eventLogId}
            onChange={handleEventIdFilter}
            allowClear
            suffixIcon={<FilterOutlined />}
            size="large"
            showSearch
            optionFilterProp="children"
            loading={lookupsLoading}
          >
            {eventIdOptions.map((event) => (
              <Option key={event.id} value={event.id}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Tag 
                    color={getEventColorById(event.id)} 
                    style={{ marginRight: 8, minWidth: 60, textAlign: "center" }}
                  >
                    {event.type}
                  </Tag>
                  {event.name}
                </div>
              </Option>
            ))}
          </Select>
        </Col>

        {/* Username Filter */}
        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Username"
            value={filters.username || ''}
            onChange={(e) => setFilters({...filters, username: e.target.value})}
            onPressEnter={() => handleUsernameFilter(filters.username)}
            onBlur={() => handleUsernameFilter(filters.username)}
            suffix={<FilterOutlined />}
            size="large"
          />
        </Col>

      {/* Date Range Filter */}
      <Col xs={24} sm={12} md={6}>
        <RangePicker
          style={{ width: "100%" }}
          placeholder={["Start Date", "End Date"]}
          format="YYYY-MM-DD HH:mm:ss"
          showTime={{ format: 'HH:mm:ss' }}
          onChange={handleDateRangeFilter}
          suffixIcon={<CalendarOutlined />}
          size="large"
          // Set default value to today
          defaultValue={[
            dayjs().startOf('day'),
            dayjs().endOf('day')
          ]}
          // Optional: You can also set value prop if you want it controlled
          value={filters.dateRange ? [
            dayjs(filters.dateRange.startDate),
            dayjs(filters.dateRange.endDate)
          ] : [
            dayjs().startOf('day'),
            dayjs().endOf('day')
          ]}
        />
      </Col>

        {/* Role ID Filter */}
        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Select Role"
            style={{ width: "100%" }}
            value={filters.userRoleId}
            onChange={handleRoleIdFilter}
            allowClear
            suffixIcon={<FilterOutlined />}
            size="large"
            showSearch
            optionFilterProp="children"
          >
            {roleIdOptions.map((role) => (
              <Option key={role.id} value={role.id}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Tag 
                    color={getRoleColorByName(role.name)} 
                    style={{ marginRight: 8, minWidth: 60, textAlign: "center" }}
                  >
                    {role.name}
                  </Tag>
                  {role.name}
                </div>
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      {/* RESET BUTTON */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24}>
          <Button 
            style={{ width: "100%" }} 
            onClick={handleResetFilters}
            size="large"
            type="default"
            danger
          >
            Reset All Filters
          </Button>
        </Col>
      </Row>

      {/* COUNTS */}
      <div style={{ marginBottom: 16 }}>
        <Typography.Text strong style={{ marginRight: 16 }}>
          Total Events: {events.length}
        </Typography.Text>
        
        {filteredEvents.length !== events.length && (
          <Typography.Text type="secondary" style={{ marginRight: 16 }}>
            Filtered: {filteredEvents.length}
          </Typography.Text>
        )}
        
        {Object.values(filters).some(f => f !== null && f !== undefined && f !== '') && (
          <Tag color="blue" style={{ marginRight: 8 }}>
            Backend Filters Active
          </Tag>
        )}
      </div>

      {/* TABLE */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
            Loading events from server...
          </Typography.Text>
        </div>
      ) : filteredEvents.length === 0 ? (
        <Empty 
          description={
            events.length === 0 
              ? "No events found with current filters" 
              : "No events match your local search"
          }
        />
      ) : (
        <Table
          dataSource={filteredEvents}
          columns={columns}
          rowKey="eventLogId"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} events`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          scroll={{ x: 1200 }}
        />
      )}
    </Card>
  );
};

export default EventsLogs;